<script setup>
import { ref, computed, onMounted } from "vue";
import { usePage, useForm } from "@inertiajs/vue3";
import ApplicationLogo from "@/Components/ApplicationLogo.vue";
import Dropdown from "@/Components/Dropdown.vue";
import DropdownLink from "@/Components/DropdownLink.vue";
import NavLink from "@/Components/NavLink.vue";
import ResponsiveNavLink from "@/Components/ResponsiveNavLink.vue";
import { Link } from "@inertiajs/vue3";
import CookieConsent from '@/Components/CookieConsent.vue'
import { ModalsContainer, useModal } from "vue-final-modal";

// <ModalsContainer> в SSR не рендерит ничего, а на клиенте — фрагмент.
// Из-за этого число детей у корня страницы расходилось и Vue ругался
// «Hydration completed but contains mismatches» на КАЖДОЙ странице сайта.
// С v-if обе стороны отдают комментарий-заглушку, а контейнер появляется после монтирования.
const vfmReady = ref(false);
onMounted(() => { vfmReady.value = true; });

import CitySet from "@/Components/CitySet.vue";
import ModalPhoneConfirm from "@/Components/ModalPhoneConfirm.vue";
import LoginModal from "@/Components/LoginModal.vue";

const showingNavigationDropdown = ref(false);
const cabinetOpen = ref(false);
const addOpen = ref(false);

/**
 * Меню «Добавить».
 *
 * Раньше в шапке стояли две одинаковые индиговые кнопки — «объявление» и
 * «пост», — с одним и тем же плюсом в кружке. Третий пункт («дело») сделал бы
 * из них сплошную индиговую полосу: самый насыщенный цвет шапки уходил на
 * второстепенные действия, да ещё и повторялся.
 *
 * Подписи с пояснением обязательны. «Дело» само по себе непонятно: это может
 * быть и заявка, и клиентское дело, и что угодно ещё. Строка «Решение суда в
 * базу практики» снимает вопрос до нажатия.
 */
const addItems = computed(() => {
  const a = props.auth;
  if (!a) return [];
  const items = [];
  items.push({
    href: '/articlesadd',
    title: 'Пост',
    note: 'Статья или разбор в ленту',
    icon: 'post',
  });
  if (a.lawyer == 1) items.push({
    href: '/uslugiadd',
    title: 'Объявление',
    note: 'Услуга в каталоге с ценой',
    icon: 'offer',
  });
  // Дело добавляет и юрист — свою практику ведёт он сам, а не редакция.
  if (a.isadmin || a.lawyer == 1) items.push({
    href: '/admin/court/new',
    title: 'Дело',
    note: 'Решение суда в базу практики',
    icon: 'case',
  });
  return items;
});
const loginOpen = ref(false);
const loginForm = useForm({
  email: '',
  password: '',
  remember: true,
  redirect: typeof window !== 'undefined' ? window.location.href : '',
});

const submitLogin = () => {
  loginForm.post(route('login'), {
    onFinish: () => loginForm.reset('password'),
    onSuccess: () => { loginOpen.value = false; },
  });
};

const page = usePage();

const headerPhone = "8 (978) 8838 978";
const headerPhoneTo = "tel:+79788838978";
const headerPhoneMasked = "8 (978) 8838 9**";

let props = defineProps({
  auth: Object,
  city: Object,
  profile: Boolean,
  hideBtn: Boolean,
  reloadpage: Boolean,
  mainurl: [Number, String],
  secondurl: String,
  backendurl: String,
  showPhone: {
    type: Boolean,
    default: false,
  },
  tracking: {
    type: Object,
    default: () => ({}),
  },
});

const cityTitle = computed(() =>
  props.city?.title
    ?? page.props.usercity?.title
    ?? (props.auth?.city && props.auth.city != 0 ? props.auth.city : null)
);

const { open, close } = useModal({
  component: CitySet,
  attrs: {
    profile: props.profile,
    mainurl: props.mainurl,
    secondurl: props.secondurl,
    modalPageTitle: "city",
    reloadpage: props.reloadpage,
    onConfirm() {
      close();
    },
  },
});

defineExpose({ open });

const { open: openPhone, close: closePhone } = useModal({
  component: ModalPhoneConfirm,
  attrs: {
    phone: headerPhone,
    phoneto: headerPhoneTo,
    onClose() { closePhone(); },
  },
});

const { open: openLogin, close: closeLogin } = useModal({
  component: LoginModal,
  attrs: {
    onConfirm() { closeLogin(); },
  },
});
</script>

<template>
  <div class="mh sticky top-0 z-50" id="mh">
    <div class="bg-gray-100">
      <nav class="bg-white border-b border-gray-100 shadow-sm">
        <!-- Primary Navigation Menu -->
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="flex justify-between h-12">
            <div class="flex">
              <!-- Logo -->
              <div class="flex items-center">
                <Link
                  :href="route('Welcome')"
                  class="flex justify-center items-center h-12 pt-0"
                  aria-label="Home"
                >
                  <ApplicationLogo
                    class="block w-auto fill-current text-gray-800"
                  />
                </Link>
              </div>

              <!-- Navigation Links -->
              <div class="hidden sm:flex items-center ml-4 lg:ml-6 gap-1 lg:gap-3">
                <NavLink :href="route('Welcome')" :active="route().current('Welcome')">
                  <span class="whitespace-nowrap">Главная</span>
                </NavLink>
                <NavLink :href="route('lenta.popular')" :active="route().current('lenta.popular')">
                  <span class="whitespace-nowrap">Лента</span>
                </NavLink>
                <NavLink :href="route('questions.add')" :active="route().current('questions.add')">
                  <span class="whitespace-nowrap">Задать вопрос</span>
                </NavLink>
                <NavLink :href="route('uslugi')" :active="route().current('uslugi')">
                  <span class="whitespace-nowrap">Найти юриста</span>
                </NavLink>
                <NavLink :href="route('tools.sudrf')" :active="route().current('tools.sudrf')">
                  <span class="whitespace-nowrap">Поиск акта</span>
                </NavLink>
                <div class="hidden lg:block relative">
                  <button
                    type="button"
                    @click="cabinetOpen = !cabinetOpen"
                    class="inline-flex items-center gap-1 px-1 pt-1 border-b-2 border-transparent text-sm leading-4 text-gray-500 hover:text-gray-700 focus:outline-none transition whitespace-nowrap"
                  >
                    Кабинет
                  </button>
                  <div v-show="cabinetOpen" class="fixed inset-0 z-40" @click="cabinetOpen = false"></div>
                  <div
                    v-show="cabinetOpen"
                    class="absolute left-0 mt-2 w-48 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 py-1"
                  >
                    <a :href="route('clientdashboard')" class="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                      </svg>
                      Кабинет клиента
                    </a>
                    <a :href="route('lawyerdashboard')" class="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"/>
                      </svg>
                      Кабинет юриста
                    </a>
                    <a v-if="props.auth && (props.auth.lawyer == 1 || props.auth.is_editor == 1)" :href="route('my.articles')" class="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10l6 6v8a2 2 0 01-2 2zM7 9h5M7 13h10M7 17h10"/>
                      </svg>
                      Мои материалы
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div class="hidden sm:flex sm:items-center sm:ml-6">
              <!-- Phone button (only on pages that opt in) -->
              <button
                v-if="props.showPhone"
                onclick="window.goal('OPEN_PHONE');"
                @click="openPhone"
                class="mr-3 inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-brand-200 focus:ring-offset-2 whitespace-nowrap"
              >
                <svg class="w-4 h-4 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                </svg>
                {{ headerPhoneMasked }}
              </button>
              <!-- Settings Dropdown -->
              <div class="ml-3 relative flex items-center">
                <!-- МЕНЮ «ДОБАВИТЬ».
                     Если пункт всего один — показываем прямую ссылку: раскрывать
                     меню ради единственной строки значит просить лишнее нажатие
                     без всякой причины. -->
                <div v-if="addItems.length" class="relative">
                  <a
                    v-if="addItems.length === 1"
                    :href="addItems[0].href"
                    class="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition-colors"
                  >
                    <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 5v14M5 12h14" />
                    </svg>
                    {{ addItems[0].title }}
                  </a>

                  <template v-else>
                    <button
                      type="button"
                      @click="addOpen = !addOpen"
                      :aria-expanded="addOpen"
                      aria-haspopup="true"
                      class="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition-colors"
                    >
                      <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 5v14M5 12h14" />
                      </svg>
                      Добавить
                      <svg class="w-3 h-3 shrink-0 transition-transform" :class="addOpen ? 'rotate-180' : ''"
                           fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    <div v-show="addOpen" class="fixed inset-0 z-40" @click="addOpen = false"></div>
                    <div
                      v-show="addOpen"
                      @keydown.esc="addOpen = false"
                      class="absolute right-0 mt-2 w-64 rounded-xl shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 py-1.5"
                    >
                      <a
                        v-for="it in addItems"
                        :key="it.href"
                        :href="it.href"
                        class="flex items-start gap-3 px-3.5 py-2.5 hover:bg-indigo-50 transition-colors group/add"
                      >
                        <span class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 group-hover/add:bg-indigo-100 group-hover/add:text-indigo-600 transition-colors">
                          <svg v-if="it.icon === 'post'" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 7.5h6M12 12h6m-6 4.5h3M4.5 5.25h15a.75.75 0 0 1 .75.75v12a.75.75 0 0 1-.75.75h-15a.75.75 0 0 1-.75-.75V6a.75.75 0 0 1 .75-.75Z" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 7.5h2.5v3h-2.5z" />
                          </svg>
                          <svg v-else-if="it.icon === 'offer'" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 6h.008v.008H6V6Z" />
                          </svg>
                          <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v18M3.75 7.5h16.5M6.75 7.5 4.5 13.5h4.5L6.75 7.5Zm10.5 0L15 13.5h4.5L17.25 7.5ZM4.5 13.5a2.25 2.25 0 0 0 4.5 0m6 0a2.25 2.25 0 0 0 4.5 0M8.25 21h7.5" />
                          </svg>
                        </span>
                        <span class="min-w-0">
                          <span class="block text-sm font-semibold text-gray-900">{{ it.title }}</span>
                          <span class="block text-xs text-gray-500 leading-snug">{{ it.note }}</span>
                        </span>
                      </a>
                    </div>
                  </template>
                </div>

                <button
                  v-if="!hideBtn"
                  type="button"
                  @click="() => open()"
                  aria-label="city"
                  class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-lg text-gray-500 bg-white hover:text-gray-700 focus:outline-none transition ease-in-out duration-150"
                >
                  <span v-if="cityTitle">{{ cityTitle }}</span>
                  <span v-else>город</span>
                </button>

                <Dropdown align="right" width="48" v-if="props.auth">
                  <template #trigger>
                    <span class="inline-flex rounded-lg">
                      <button
                        type="button"
                        aria-label="enter"
                        class="inline-flex items-center gap-2 px-2 py-1.5 border border-transparent rounded-lg hover:bg-gray-50 focus:outline-none transition ease-in-out duration-150"
                      >
                        <!-- Аватарка -->
                        <img
                          v-if="props.auth.avatar_path && props.auth.avatar_path !== 'storage/default/avatar.webp'"
                          :src="`/${props.auth.avatar_path}`"
                          class="w-7 h-7 rounded-full object-cover shrink-0"
                          alt=""
                        />
                        <img
                          v-else-if="props.auth.default_avatar_id"
                          :src="`https://avatars.yandex.net/get-yapic/${props.auth.default_avatar_id}/islands-50`"
                          class="w-7 h-7 rounded-full object-cover shrink-0"
                          alt=""
                        />
                        <span
                          v-else
                          class="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold flex items-center justify-center shrink-0"
                        >{{ props.auth.name?.charAt(0).toUpperCase() }}</span>

                        <!-- Первое слово имени -->
                        <span class="text-sm text-gray-700 hidden lg:inline">{{ props.auth.name?.split(' ')[0] }}</span>

                        <svg class="-mr-0.5 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                      </button>
                    </span>
                  </template>

                  <template #content>
                    <div>
                      <DropdownLink
                        v-if="props.auth.lawyer == 1"
                        :href="route('lawyer', props.auth.id)"
                      >
                        Мой профиль
                      </DropdownLink>
                      <!-- Статьи и разборы практики юриста: до 16.09.2026 на эту страницу не вело ни одной ссылки -->
                      <DropdownLink
                        v-if="props.auth.lawyer == 1 || props.auth.is_editor == 1"
                        :href="route('my.articles')"
                      >
                        Мои материалы
                      </DropdownLink>
                      <DropdownLink :href="route('lenta.bookmarked')">
                        Закладки
                      </DropdownLink>
                      <DropdownLink
                        v-if="props.auth.lawyer == 0"
                        :href="route('my.questions')"
                      >
                        Мои вопросы
                      </DropdownLink>

                      <DropdownLink
                        v-if="props.auth.isadmin"
                        :href="route('admin.articles.list')"
                      >
                        Все статьи
                      </DropdownLink>

                      <DropdownLink
                        v-if="props.auth.isadmin"
                        :href="route('admin.uslugi.list')"
                      >
                        Все услуги
                      </DropdownLink>

                      <!--
                        ⚠ ОБСТОЯТЕЛЬСТВА И «КАК УСТРОЕНО» ИЗ ЭТОГО МЕНЮ УБРАНЫ (18.09.2026):
                        вход в них стоит на самой странице практики, в панели редакции, —
                        там, где человек уже работает с делами. Меню профиля от дублей
                        разрасталось, а искали их всё равно на странице.
                      -->
                      <DropdownLink
                        v-if="props.auth.isadmin"
                        :href="route('admin.court')"
                      >
                        Судебная практика
                      </DropdownLink>

                      <DropdownLink
                        v-if="props.auth.isadmin"
                        :href="route('admin.perks')"
                      >
                        Значки юристов
                      </DropdownLink>

                      <DropdownLink
                        v-if="props.auth.isadmin"
                        :href="route('seo.core')"
                      >
                        Семантическое ядро
                      </DropdownLink>

                      <DropdownLink
                        v-if="props.auth.isadmin"
                        :href="route('admin.situations')"
                      >
                        Ситуации
                      </DropdownLink>

                      <DropdownLink
                        v-if="props.auth.isadmin"
                        :href="route('messages')"
                      >
                        Сообщения
                      </DropdownLink>
                      <DropdownLink
                        v-if="props.auth.isadmin"
                        :href="route('admin.settings')"
                      >
                        Настройки
                      </DropdownLink>
                      <DropdownLink v-if="props.auth.lawyer != 1" :href="route('profile.edit')">
                        Профиль
                      </DropdownLink>
                      <DropdownLink
                        :href="route('logout')"
                        method="post"
                        as="button"
                        >Выйти</DropdownLink
                      >
                    </div>
                  </template>
                </Dropdown>

                <div v-if="!props.auth" class="relative">
                  <button
                    type="button"
                    @click="loginOpen = !loginOpen"
                    class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-lg text-gray-500 bg-white hover:text-gray-700 focus:outline-none transition ease-in-out duration-150"
                  >
                    Войти
                  </button>
                  <div v-show="loginOpen" class="fixed inset-0 z-40" @click="loginOpen = false"></div>
                  <div v-show="loginOpen" class="absolute right-0 mt-2 w-80 rounded-xl shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 p-4 space-y-3">

                    <!-- Яндекс ID -->
                    <a
                      :href="`/auth/yandex?from=${encodeURIComponent($page.url)}`"
                      class="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-lg bg-black hover:bg-gray-900 text-white text-sm font-medium transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-5 h-5 shrink-0">
                        <path fill="white" d="M2.04 12c0-5.523 4.476-10 9.998-10C17.522 2 22 6.477 22 12s-4.478 10-10.002 10C6.516 22 2.04 17.523 2.04 12Z"/>
                        <path fill="#FC3F1D" d="M13.32 7.666h-.924c-1.694 0-2.585.858-2.585 2.123 0 1.43.616 2.1 1.881 2.959l1.045.704-3.003 4.548H7.919l2.771-4.133c-1.573-1.12-2.464-2.21-2.464-4.02 0-2.288 1.573-3.838 4.576-3.838h2.464v11.99H13.32V7.666Z"/>
                      </svg>
                      Войти через Яндекс ID
                    </a>

                    <div class="flex items-center gap-2 text-xs text-gray-500">
                      <div class="flex-1 h-px bg-gray-200"></div>
                      или войдите по email
                      <div class="flex-1 h-px bg-gray-200"></div>
                    </div>

                    <!-- Email форма -->
                    <form @submit.prevent="submitLogin" class="space-y-3">
                      <div>
                        <input
                          type="email"
                          v-model="loginForm.email"
                          placeholder="Email"
                          required
                          autofocus
                          autocomplete="username"
                          class="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        />
                        <p v-if="loginForm.errors.email" class="mt-1 text-xs text-red-600">{{ loginForm.errors.email }}</p>
                      </div>
                      <div>
                        <input
                          type="password"
                          v-model="loginForm.password"
                          placeholder="Пароль"
                          required
                          autocomplete="current-password"
                          class="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        />
                        <p v-if="loginForm.errors.password" class="mt-1 text-xs text-red-600">{{ loginForm.errors.password }}</p>
                      </div>
                      <button
                        type="submit"
                        :disabled="loginForm.processing"
                        class="w-full py-2 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <svg v-if="loginForm.processing" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                        {{ loginForm.processing ? 'Входим...' : 'Войти' }}
                      </button>
                      <div class="flex justify-between text-xs text-gray-500">
                        <Link :href="route('password.request')" class="hover:underline hover:text-gray-700">Забыли пароль?</Link>
                        <Link :href="route('register')" class="hover:underline hover:text-gray-700">Регистрация</Link>
                      </div>
                      <p class="text-xs text-gray-500 leading-tight">
                        <Link href="/personal" class="underline hover:text-gray-600">Согласие на обработку персональных данных</Link> получено при регистрации.
                      </p>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            <!-- Hamburger -->
            <div class="-mr-2 flex items-center gap-1 sm:hidden">
              <!-- Город (мобильный) -->
              <button
                v-if="!hideBtn"
                type="button"
                @click="() => open()"
                aria-label="Выбрать город"
                class="inline-flex items-center gap-1 max-w-[42vw] px-2.5 py-1.5 text-sm font-medium rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 transition"
              >
                <svg class="w-4 h-4 shrink-0 text-indigo-500" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0z" />
                </svg>
                <span class="truncate">{{ cityTitle || 'Город' }}</span>
              </button>
              <button
                v-if="!props.auth"
                type="button"
                @click="openLogin"
                class="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
              >
                Войти
              </button>
              <button
                title="Mainmenu"
                @click="showingNavigationDropdown = !showingNavigationDropdown"
                class="inline-flex items-center justify-center p-2 rounded-lg text-gray-500 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500 transition duration-150 ease-in-out"
              >
                <svg
                  class="h-6 w-6"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    :class="{
                      hidden: showingNavigationDropdown,
                      'inline-flex': !showingNavigationDropdown,
                    }"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                  <path
                    :class="{
                      hidden: !showingNavigationDropdown,
                      'inline-flex': showingNavigationDropdown,
                    }"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Responsive Navigation Menu -->
        <div
          :class="{
            block: showingNavigationDropdown,
            hidden: !showingNavigationDropdown,
          }"
          class="sm:hidden"
        >
          <div v-if="props.showPhone" class="px-4 py-3">
            <button
              onclick="window.goal('OPEN_PHONE');"
              @click="openPhone"
              class="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors focus:outline-none"
            >
              <svg class="w-4 h-4 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
              </svg>
              {{ headerPhoneMasked }}
            </button>
          </div>
          <div class="pt-2 pb-3 space-y-1">
            <ResponsiveNavLink
              :href="route('Welcome')"
              :active="route().current('Welcome')"
            >
              Главная
            </ResponsiveNavLink>
            <ResponsiveNavLink
              :href="route('lenta.popular')"
              :active="route().current('lenta.popular')"
            >
              Лента
            </ResponsiveNavLink>
            <ResponsiveNavLink
              :href="route('questions.add')"
              :active="route().current('questions.add')"
            >
              Задать вопрос
            </ResponsiveNavLink>
            <ResponsiveNavLink
              :href="route('uslugi')"
              :active="route().current('uslugi')"
            >
              Найти юриста
            </ResponsiveNavLink>
            <ResponsiveNavLink
              :href="route('tools.sudrf')"
              :active="route().current('tools.sudrf')"
            >
              Поиск судебного акта
            </ResponsiveNavLink>

            <div class="px-4 pt-2 pb-1 text-xs font-semibold text-gray-500 uppercase tracking-widest">Кабинет</div>
            <ResponsiveNavLink
              :href="route('clientdashboard')"
              :active="route().current('clientdashboard')"
            >
              — Кабинет клиента
            </ResponsiveNavLink>
            <ResponsiveNavLink
              :href="route('lawyerdashboard')"
              :active="route().current('lawyerdashboard')"
            >
              — Кабинет юриста
            </ResponsiveNavLink>
          </div>

          <!-- «Добавить» на телефоне: раскрывающееся меню тут не нужно, шторка
               и так список. Показываем те же пункты с теми же пояснениями. -->
          <div v-if="addItems.length" class="pt-3 border-t border-gray-200">
            <div class="px-4 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Добавить</div>
            <ResponsiveNavLink v-for="it in addItems" :key="it.href" :href="it.href">
              {{ it.title }}
              <span class="block text-xs text-gray-500">{{ it.note }}</span>
            </ResponsiveNavLink>
          </div>

          <!-- Responsive Settings Options -->
          <div class="pt-4 border-t border-gray-200">
            <div v-if="props.auth">
              <ResponsiveNavLink :href="route('profile.edit')">
                Профиль - {{ props.auth.name }}
              </ResponsiveNavLink>

              <ResponsiveNavLink v-if="props.auth.lawyer == 1 || props.auth.is_editor == 1" :href="route('my.articles')">
                Мои материалы
              </ResponsiveNavLink>

              <ResponsiveNavLink :href="route('my.questions')">
                Мои вопросы
              </ResponsiveNavLink>

              <ResponsiveNavLink :href="route('lenta.bookmarked')">
                Закладки
              </ResponsiveNavLink>

              <ResponsiveNavLink
                :href="route('logout')"
                method="post"
                as="button"
              >
                Выйти
              </ResponsiveNavLink>
            </div>

            <div v-else>
              <ResponsiveNavLink :href="route('lenta.bookmarked')">
                Закладки
              </ResponsiveNavLink>
              <button
                type="button"
                @click="openLogin"
                class="block w-full pl-3 pr-4 py-2 border-l-4 border-transparent text-left text-base font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 hover:border-gray-300 focus:outline-none transition"
              >
                Войти
              </button>
            </div>
          </div>
        </div>
      </nav>
    </div>
  </div>
  <div v-if="page.props?.dbHost" class="fixed bottom-2 right-2 z-50 text-xs px-2 py-1 rounded-lg shadow" :class="page.props?.dbHost === '127.0.0.1' ? 'bg-green-500 text-white' : 'bg-orange-500 text-white'">
    DB: {{ page.props?.dbHost }}
  </div>
  <ModalsContainer v-if="vfmReady" />

  <CookieConsent :auth="props.auth" />
</template>